<section id="summary" class="section reveal reveal--fade-up" s2eReveal aria-labelledby="summary-heading">
  <div class="section__head">
    <div class="section__headings">
      <h2 class="section__title" id="summary-heading">{{ 'summary.title' | reportT }}</h2>
    </div>
  </div>

  <div class="summary-grid">
    <!-- 1 — La note, au cadran. -->
    <article class="card card--interactive summary-card summary-card--hero" data-print-block>
      <div class="summary-card__head">
        <div class="summary-card__labels">
          <p class="summary-card__label">{{ 'summary.score' | reportT }}</p>
          <p class="summary-card__hint">{{ 'summary.scoreHint' | reportT }}</p>
        </div>
        <span class="summary-card__chip summary-card__chip--primary"><svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg></span>
      </div>
      <div class="summary-card__body">
        <div class="summary-card__ring-wrap">
          <!-- `.ring-meter` reste le contexte de positionnement : le bloc
               central est projeté dans le composant, qui le rend en frère du
               SVG — ce dernier est pivoté de −90°, et un centre placé dedans
               pivoterait avec lui. -->
          <div class="ring-meter" style="width:112px;height:112px" role="img" [attr.aria-label]="'summary.score' | reportT">
            <s2e-ring-meter
              [percentage]="subject().score.percentage"
              [level]="subject().score.level"
              [theme]="chrome.theme()"
            >
              <!-- La note sur 20, barème des bulletins : la carte affichait les
                   points bruts — « 10 / 27 » — sous une légende qui annonce
                   « sur 20 ». Les points restent lisibles sous le cadran. -->
              <div class="ring-meter__center">
                @if (subject().score.markOutOf20; as note) {
                  <span dir="ltr" class="ring-meter__value">
                    <span [s2eCountUp]="note" [countUpFormat]="markFormat()"></span><span class="ring-meter__unit"> / <span>{{ 20 | reportFmt: 'num' }}</span></span>
                  </span>
                } @else {
                  <span dir="ltr" class="ring-meter__value">—</span>
                }
                <span class="ring-meter__pct">{{ subject().score.percentage | reportFmt: 'pct' }}</span>
              </div>
            </s2e-ring-meter>
          </div>
        </div>
        <!-- Ce que la note résume : le barème du test lui-même. Même clé et
             mêmes variables que le tableau « toutes matières », pour que les
             deux écrivent les points de la même façon. -->
        @if (pointsVars(); as points) {
          <p dir="ltr" class="summary-card__foot fraction tabular">
            {{ 'summary.points' | reportT: points }}
          </p>
        }
      </div>
    </article>

    <!-- 2 — Le palier, et le plan qui en découle. -->
    <article class="card card--interactive summary-card" data-print-block>
      <div class="summary-card__head">
        <div class="summary-card__labels">
          <p class="summary-card__label">{{ 'summary.level' | reportT }}</p>
          <p class="summary-card__hint">{{ 'summary.levelHint' | reportT }}</p>
        </div>
        <span class="summary-card__chip summary-card__chip--warning"><svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M16 7h6v6"/><path d="m22 7-8.5 8.5-5-5L2 17"/></svg></span>
      </div>
      <div class="summary-card__body">
        @if (subject().score.band; as band) {
          <!-- Intitulés venus de l'API, déjà localisés : les paliers sont
               paramétrables en base, ils n'ont pas de clé de traduction. -->
          <p class="summary-card__value">{{ band.label }}</p>
          <span [class]="bandBadgeClass()">{{ band.planLabel }}</span>
        } @else {
          <!-- Le score ne tombe dans aucune tranche du catalogue. On le dit par
               un tiret plutôt que par un palier arbitraire : afficher
               « moyen » ici inventerait un classement que personne n'a
               prononcé. (Le bundle `REPORT` n'a pas de libellé « non classé ».) -->
          <p class="summary-card__value">—</p>
        }
      </div>
    </article>

    <!-- 3 — L'acquisition : un compte de compétences, jamais des points. -->
    <article class="card card--interactive summary-card" data-print-block>
      <div class="summary-card__head">
        <div class="summary-card__labels">
          <p class="summary-card__label">{{ 'summary.mastered' | reportT }}</p>
          <p class="summary-card__hint">{{ 'summary.masteredHint' | reportT }}</p>
        </div>
        <span class="summary-card__chip summary-card__chip--success"><svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"/><path d="M20 2v4"/><path d="M22 4h-4"/><circle cx="4" cy="20" r="2"/></svg></span>
      </div>
      <div class="summary-card__body">
        <p dir="ltr" class="summary-card__value summary-card__value--ltr"><span class="tabular">{{ subject().acquisition.acquired | reportFmt: 'num' }}</span><span class="summary-card__unit"> / <span>{{ subject().acquisition.total | reportFmt: 'num' }}</span></span></p>
      </div>
    </article>

    <!-- 4 — La durée. -->
    <article class="card card--interactive summary-card" data-print-block>
      <div class="summary-card__head">
        <div class="summary-card__labels">
          <p class="summary-card__label">{{ 'summary.duration' | reportT }}</p>
          <p class="summary-card__hint">{{ 'summary.durationHint' | reportT }}</p>
        </div>
        <span class="summary-card__chip summary-card__chip--neutral"><svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg></span>
      </div>
      <div class="summary-card__body">
        @if (duration(); as d) {
          <p class="summary-card__value">{{ d.key | reportT: d.vars }}</p>
        } @else {
          <p class="summary-card__value">—</p>
        }
      </div>
    </article>

    <!-- 5 — Le verdict de maîtrise, à ne pas confondre avec le palier. -->
    <article class="card card--interactive summary-card" data-print-block>
      <div class="summary-card__head">
        <div class="summary-card__labels">
          <p class="summary-card__label">{{ 'summary.status' | reportT }}</p>
          <p class="summary-card__hint">{{ 'summary.statusHint' | reportT }}</p>
        </div>
        <!-- La maquette figeait la croix rouge de son jeu d'essai. La pastille
             suit le verdict : une croix au-dessus de « réussi » démentirait la
             carte qu'elle coiffe. -->
        @if (subject().score.passed) {
          <span class="summary-card__chip summary-card__chip--success"><svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg></span>
        } @else {
          <span class="summary-card__chip summary-card__chip--danger"><svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg></span>
        }
      </div>
      <div class="summary-card__body">
        @if (subject().score.passed) {
          <p class="summary-card__value">{{ 'status.passed' | reportT }}</p>
          <span class="badge badge--success badge--sm"><svg class="icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg><span>{{ 'status.passed' | reportT }}</span></span>
        } @else {
          <p class="summary-card__value">{{ 'status.failed' | reportT }}</p>
          <span class="badge badge--danger badge--sm"><svg class="icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg><span>{{ 'status.failed' | reportT }}</span></span>
        }
      </div>
    </article>
  </div>
</section>
